import { parse } from "mathjs/number";
import type { VectorFn } from "~/lib/types";
import type { ComputeContext, NodeDefinition } from "~/nodes/types";

const ZERO = (): number => 0;

/**
 * Compiles one `f(t) = …` expression. `a`, `b` and `c` are baked in as constants
 * at compile time, so the returned function only takes the curve parameter.
 */
function compileAxis(
  expression: unknown,
  scope: Record<string, number>,
): (t: number) => number {
  const compiled: unknown = parse(`f(t) = ${String(expression)}`)
    .compile()
    .evaluate(scope);
  if (typeof compiled !== "function")
    throw new Error("Expression is not a function of t");

  const axis = compiled as (t: number) => unknown;

  // Probe once so a bad expression fails here rather than 2000 times per frame.
  if (typeof axis(0.1) !== "number")
    throw new Error("Expression did not return a number");

  return axis as (t: number) => number;
}

export const customNode: NodeDefinition = {
  type: "custom",
  label: "Custom",
  category: "Curves",
  width: 280,
  inputs: [
    {
      key: "a",
      label: "a",
      socket: "number",
      control: { kind: "number", key: "a", label: "a" },
    },
    {
      key: "b",
      label: "b",
      socket: "number",
      control: { kind: "number", key: "b", label: "b" },
    },
    {
      key: "c",
      label: "c",
      socket: "number",
      control: { kind: "number", key: "c", label: "c" },
    },
  ],
  outputs: [{ key: "v3", label: "Vector", socket: "vector" }],
  controls: [
    { kind: "text", key: "fx", label: "fx" },
    { kind: "text", key: "fy", label: "fy" },
    { kind: "text", key: "fz", label: "fz" },
  ],
  defaults: { a: 1, b: 1, c: 1, fx: "sin(t)", fy: "t*floor(t)", fz: "1.1^t" },

  compute(ctx: ComputeContext) {
    const scope = {
      a: ctx.number("a", 1),
      b: ctx.number("b", 1),
      c: ctx.number("c", 1),
    };

    let vector: VectorFn;
    try {
      vector = {
        x: compileAxis(ctx.data["fx"], scope),
        y: compileAxis(ctx.data["fy"], scope),
        z: compileAxis(ctx.data["fz"], scope),
      };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Invalid expression";
      for (const key of ["fx", "fy", "fz"]) ctx.fail(key, message);
      vector = { x: ZERO, y: ZERO, z: ZERO };
    }

    return { v3: vector };
  },
};
